import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { theme } from '../../styles/theme';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface YearlyData {
  year: number;
  economicValue: number;
  peopleImpacted: number;
}

interface TemporalDistributionChartProps {
  clusterName: string;
  clusterId: string;
  baseEconomicValue: number;
  basePeopleImpacted: number;
  yearlyPercentages: Array<{ year: number; percentage: number }>;
  darkMode: boolean;
  onClose: () => void;
}

const TemporalDistributionChart: React.FC<TemporalDistributionChartProps> = ({
  clusterName,
  clusterId: _clusterId,
  baseEconomicValue,
  basePeopleImpacted,
  yearlyPercentages,
  darkMode,
  onClose,
}) => {
  // Calculate cumulative values
  const calculateCumulativeData = (): YearlyData[] => {
    let cumulativeEconomic = 0;
    let cumulativePeople = 0;
    
    console.log('Temporal Distribution Debug:', {
      clusterName,
      baseEconomicValue,
      basePeopleImpacted,
      yearlyPercentages
    });
    
    return yearlyPercentages.map(({ year, percentage }) => {
      cumulativeEconomic += (baseEconomicValue * percentage) / 100;
      cumulativePeople += (basePeopleImpacted * percentage) / 100;
      
      console.log(`Year ${year}: percentage=${percentage}%, cumEcon=${cumulativeEconomic}, cumPeople=${cumulativePeople}`);
      
      return {
        year,
        economicValue: cumulativeEconomic,
        peopleImpacted: cumulativePeople,
      };
    });
  };

  const cumulativeData = calculateCumulativeData();
  const years = cumulativeData.map(d => `Year ${d.year}`);
  
  // Format values for display
  const formatEconomicValue = (value: number): string => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatPeople = (value: number): string => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
    return value.toFixed(0);
  };

  const data = {
    labels: years,
    datasets: [
      {
        label: 'Economic Value',
        data: cumulativeData.map(d => d.economicValue),
        borderColor: theme.colors.primary.main,
        backgroundColor: theme.colors.primary.main + '20',
        yAxisID: 'y1',
        tension: 0.4, // Smooth curve
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'People Impacted',
        data: cumulativeData.map(d => d.peopleImpacted),
        borderColor: theme.colors.status.success,
        backgroundColor: theme.colors.status.success + '20',
        yAxisID: 'y2',
        tension: 0.4, // Smooth curve
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: darkMode ? '#e5e5e5' : '#333',
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: darkMode ? '#fff' : '#000',
        bodyColor: darkMode ? '#e5e5e5' : '#333',
        borderColor: darkMode ? '#444' : '#ddd',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context: TooltipItem<'line'>) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;

            if (value === null) return `${label}: N/A`;

            if (label === 'Economic Value') {
              return `${label}: ${formatEconomicValue(value)}`;
            } else {
              return `${label}: ${formatPeople(value)} people`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: darkMode ? '#b3b3b3' : '#666',
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Economic Value ($)',
          color: darkMode ? '#e5e5e5' : '#333',
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: darkMode ? '#b3b3b3' : '#666',
          callback: function(value: any) {
            return formatEconomicValue(value as number);
          },
        },
      },
      y2: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'People Impacted',
          color: darkMode ? '#e5e5e5' : '#333',
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: darkMode ? '#b3b3b3' : '#666',
          callback: function(value: any) {
            return formatPeople(value as number);
          },
        },
      },
    },
  };

  return (
    <div 
      className="mt-6 p-6 rounded-lg shadow-lg"
      style={{
        backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
        border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold" style={{
          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
        }}>
          Temporal Impact Distribution - {clusterName}
        </h3>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Close chart"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="mb-4">
        <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
          Cumulative impact projection over 6 years
        </p>
      </div>

      <div style={{ height: '400px' }}>
        <Line data={data} options={options} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="p-3 rounded" style={{
          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        }}>
          <p className="text-xs" style={{ color: theme.colors.text.secondary }}>
            Total Economic Value (Year 6)
          </p>
          <p className="text-lg font-semibold" style={{
            color: theme.colors.primary.main,
          }}>
            {formatEconomicValue(cumulativeData[cumulativeData.length - 1]?.economicValue || 0)}
          </p>
        </div>
        <div className="p-3 rounded" style={{
          backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        }}>
          <p className="text-xs" style={{ color: theme.colors.text.secondary }}>
            Total People Impacted (Year 6)
          </p>
          <p className="text-lg font-semibold" style={{
            color: theme.colors.status.success,
          }}>
            {formatPeople(cumulativeData[cumulativeData.length - 1]?.peopleImpacted || 0)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TemporalDistributionChart;